import { ChartData, ChartOptions, Plugin } from 'chart.js'
import { getBaseChartOptions, CHART_COLORS, getMaxValueWithPadding, createDataLabelsPlugin } from '../common'
import { EnhancementViewLevel } from '../../ReportViewLevelSelector'

const COLORS = {
  submitted: CHART_COLORS.primary,
  overwritten: CHART_COLORS.tertiary,
  percentage: CHART_COLORS.secondary
} as const

export const getMaxValue = (actualValues: (number | null)[]): number =>
  getMaxValueWithPadding(actualValues, 100)

export const getChartOptions = (
  overwrittenPercentages: number[] = [],
  viewLevel: EnhancementViewLevel = EnhancementViewLevel.DEPARTMENT
): ChartOptions => {
  const maxPercentageValue = getMaxValue(overwrittenPercentages)
  
  const getXAxisTitle = () => {
    switch (viewLevel) {
      case EnhancementViewLevel.TEAM:
        return 'Teams'
      case EnhancementViewLevel.EMPLOYEE:
        return 'Employees (Top 20)'
      case EnhancementViewLevel.DEPARTMENT:
      default:
        return 'Departments'
    }
  }
  
  return {
    ...getBaseChartOptions(),
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Number of Enhancements',
          font: {
            weight: 'bold' as const
          }
        },
        beginAtZero: true,
        stacked: true,
        ticks: {
          callback(value) {
            return typeof value === 'number' ? `${value}` : ''
          }
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        grid: {
          drawOnChartArea: false,
          drawBorder: true,
          color: (context) => {
            if (context.tick && context.tick.value > 100) {
              return 'rgba(0,0,0,0)'
            }
            return 'rgba(0,0,0,0.1)'
          }
        },
        title: {
          display: true,
          text: 'Overwritten (%)',
          font: {
            weight: 'bold' as const
          }
        },
        beginAtZero: true,
        stacked: true,
        max: maxPercentageValue,
        ticks: {
          callback(value) {
            return typeof value === 'number' && value <= 100 ? `${value}%` : ''
          },
          stepSize: 10,
        },
      },
      x: {
        title: {
          display: true,
          text: getXAxisTitle(),
          font: {
            weight: 'bold' as const
          }
        }
      }
    },
    plugins: {
      ...getBaseChartOptions().plugins,
      tooltip: {
        callbacks: {
          label(context) {
            const label = context.dataset.label || ''
            const value = context.parsed.y

            if (context.dataset.yAxisID === 'y1') {
              return `${label}: ${value.toFixed(2)}%`
            }

            return `${label}: ${value}`
          }
        }
      }
    },
  }
}

export const getChartDataConfig = (
  departments: string[],
  submittedEnhancements: number[],
  overwrittenEnhancements: number[],
  overwrittenPercentages: number[]
): ChartData<'bar' | 'line'> => ({
  labels: departments,
  datasets: [
    {
      type: 'bar' as const,
      label: 'Submitted Enhancements',
      data: submittedEnhancements,
      backgroundColor: COLORS.submitted.background,
      borderColor: COLORS.submitted.border,
      borderWidth: 1,
      yAxisID: 'y',
      stack: 'Stack 0',
    },
    {
      type: 'bar' as const,
      label: 'Overwritten Enhancements',
      data: overwrittenEnhancements,
      backgroundColor: COLORS.overwritten.background,
      borderColor: COLORS.overwritten.border,
      borderWidth: 1,
      stack: 'Stack 0',
      yAxisID: 'y',
    },
    {
      type: 'line' as const,
      label: 'Overwritten (%)',
      data: overwrittenPercentages,
      borderColor: COLORS.percentage.border,
      backgroundColor: COLORS.percentage.background,
      borderWidth: 2,
      yAxisID: 'y1',
      tension: 0.1,
    },
  ],
})